import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { toast } from 'react-hot-toast';
import { generateEmiToken, getAllPlans, getAllEmiUsers } from '../../services/operations/premium';
import { useDispatch, useSelector } from 'react-redux';

const ManageEmiOption = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('generate');
    const [generatedLink, setGeneratedLink] = useState('');
    const [generatedToken, setGeneratedToken] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Form states
    const [generateEmail, setGenerateEmail] = useState('');
    const [generateAmount, setGenerateAmount] = useState('');
    const [generateCurrency, setGenerateCurrency] = useState('INR');
    
    // Error states
    const [generateEmailError, setGenerateEmailError] = useState('');
    const [generateAmountError, setGenerateAmountError] = useState('');
    
    // Result states
    const [generateResult, setGenerateResult] = useState({ status: null, message: '' });
    
    // Loading states
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // EMI Users state
    const [emiUsers, setEmiUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const CURRENCY_OPTIONS = [
        { value: 'INR', label: '₹ (INR)', symbol: '₹' },
        { value: 'USD', label: '$ (USD)', symbol: '$' }
    ];

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getAllPlans());
    }, [dispatch]);
    
    const { allPlans } = useSelector((state) => state.premium);

    // Function to get discounted price based on currency
    const getDiscountedPrice = (currency) => {
        const pinnaclePlan = allPlans?.find(plan => plan.name === "Pinnacle");
        if (!pinnaclePlan) return 0;

        const basePrice = currency === 'INR' ? pinnaclePlan.price : pinnaclePlan.dollar_price;
        const discountedPrice = basePrice * 0.7;
       
        return discountedPrice.toFixed(2);
    };

    useEffect(() => {
        const pinnaclePlan = allPlans?.find(plan => plan.name === "Pinnacle");
        if (pinnaclePlan) {
            const discountedPrice = getDiscountedPrice(generateCurrency);
            setGenerateAmount(discountedPrice);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPlans, generateCurrency]);

    const handleCurrencyChange = (newCurrency) => {
        setGenerateCurrency(newCurrency);
        const newAmount = getDiscountedPrice(newCurrency);
        setGenerateAmount(newAmount);
    };

    const getCurrencySymbol = (currency) => {
        return CURRENCY_OPTIONS.find(opt => opt.value === currency)?.symbol || '₹';
    };

    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
        // Reset all forms and states when changing tabs
        setGenerateEmail('');
        setGenerateEmailError('');
        setGenerateAmountError('');
        setGeneratedLink('');
        setGeneratedToken('');
        setGenerateResult({ status: null, message: '' });
    };

    const validateEmail = (email) => {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return emailRegex.test(email);
    };

    const handleGenerateSubmit = async (e) => {
        e.preventDefault();
        
        // Reset previous results
        setGenerateResult({ status: null, message: '' });
        setGeneratedLink('');
        setGeneratedToken('');
        
        // Validate email
        if (!generateEmail.trim()) {
            setGenerateEmailError('Email is required');
            return;
        }
        
        if (!validateEmail(generateEmail)) {
            setGenerateEmailError('Invalid email address');
            return;
        }

        // Validate amount
        if (!generateAmount.trim()) {
            setGenerateAmountError('Amount is required');
            return;
        }

        const amount = parseFloat(generateAmount);
        if (isNaN(amount) || amount <= 0) {
            setGenerateAmountError('Please enter a valid amount greater than 0');
            return;
        }
        
        setGenerateEmailError('');
        setGenerateAmountError('');
        setIsGenerating(true);
        
        try {
            const response = await generateEmiToken(generateEmail, amount, generateCurrency);
            if (response?.data?.success) {
                setGeneratedToken(response.data.token);
                setGeneratedLink(response.data.paymentLink);
                setGenerateResult({ 
                    status: 'success', 
                    message: `Payment link successfully generated for ${generateEmail} with amount ${getCurrencySymbol(generateCurrency)}${amount}` 
                });
                toast.success('Payment link generated successfully');
            } else {
                setGenerateResult({ 
                    status: 'error', 
                    message: response?.data?.message || 'User not found or unable to generate payment link' 
                });
                toast.error(response?.data?.message || 'Failed to generate payment link');
            }
        } catch (error) {
            console.error('Error generating token:', error);
            setGenerateResult({ 
                status: 'error', 
                message: error.response?.data?.message || 'Failed to connect to server. Please try again.' 
            });
            toast.error(error.response?.data?.message || 'Failed to generate payment link');
        } finally {
            setIsGenerating(false);
        }
    };

    // Fetch EMI Users
    const fetchEmiUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const response = await getAllEmiUsers();
            if (response?.data?.success) {
                setEmiUsers(response.data.emiUsers);
            } else {
                toast.error('Failed to fetch EMI users');
            }
        } catch (error) {
            console.error('Error fetching EMI users:', error);
            toast.error('Failed to fetch EMI users');
        } finally {
            setIsLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'list') {
            fetchEmiUsers();
        }
    }, [activeTab]);

    const filteredUsers = emiUsers.filter(user => 
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) &&
        (statusFilter === 'all' || user.status === statusFilter)
    );

    const tabs = [
        { label: 'Generate Payment Link', value: 'generate' },
        { label: 'EMI Users List', value: 'list' }
    ];

    const handleRowClick = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    return (
        <div className="flex dark:bg-dark font-primary h-screen overflow-hidden bg-[#fafafa]">
            <Sidebar type="manage-premium" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <main>
                    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
                        <div className="mb-8">
                            <div className="flex justify-center bg-zinc-900 rounded-full mx-auto w-full max-w-2xl">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.value}
                                        onClick={() => handleTabChange(tab.value)}
                                        className={`relative w-full px-4 py-2 rounded-full transition-colors duration-200 ${
                                            activeTab === tab.value ? 'text-white bg-zinc-800' : 'text-zinc-500 hover:text-zinc-400'
                                        }`}
                                    >
                                        {activeTab === tab.value && (
                                            <div className="absolute inset-0 w-full border-2 border-zinc-700 rounded-full" />
                                        )}
                                        <span className="relative z-10">{tab.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {activeTab === 'generate' && (
                            <div className="bg-white dark:bg-dark_50 rounded-lg shadow-lg p-6">
                                <form onSubmit={handleGenerateSubmit} className="space-y-6">
                                    <div className="flex flex-wrap -mx-3 mb-4">
                                        <span className="px-3 text-lg font-semibold text-light_30 mb-2 dark:text-zinc-300">
                                            Generate Payment Link
                                        </span>
                                        <div className="w-full px-3 mb-4">
                                            <input
                                                type="email"
                                                className="form-input w-full text-gray-300"
                                                placeholder="Enter email address"
                                                value={generateEmail}
                                                onChange={(e) => setGenerateEmail(e.target.value)}
                                                disabled={isGenerating}
                                            />
                                            {generateEmailError && (
                                                <p className="text-red-600 text-sm italic mt-1">
                                                    {generateEmailError}
                                                </p>
                                            )}
                                        </div>
                                        <div className="w-full px-3 mb-4">
                                            <div className="flex gap-4">
                                                <div className="flex-1">
                                                    <input
                                                        type="number"
                                                        className="form-input w-full text-gray-300"
                                                        placeholder="Enter total amount"
                                                        value={generateAmount}
                                                        onChange={(e) => setGenerateAmount(e.target.value)}
                                                        disabled={isGenerating}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {generateAmountError && (
                                                        <p className="text-red-600 text-sm italic mt-1">
                                                            {generateAmountError}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="w-32">
                                                    <select
                                                        className="form-input w-full text-gray-300"
                                                        value={generateCurrency}
                                                        onChange={(e) => handleCurrencyChange(e.target.value)}
                                                        disabled={isGenerating}
                                                    >
                                                        {CURRENCY_OPTIONS.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-3 mb-4">
                                        <div className="w-full px-3">
                                            <button 
                                                type="submit" 
                                                className="btn-brand-2 w-full"
                                                disabled={isGenerating}
                                            >
                                                {isGenerating ? (
                                                    <span className="flex items-center justify-center">
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Generating...
                                                    </span>
                                                ) : 'Generate Link'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                
                                <StatusIndicator status={generateResult.status} message={generateResult.message} />
                                
                                {generatedLink && (
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Payment Link:</p>
                                        <div className="flex justify-between w-full p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 dark:bg-dark_50 bg-[#f5f5f5]">
                                            <span className="w-full max-w-11/12 overflow-hidden text-ellipsis text-zinc-700 dark:text-zinc-300">{generatedLink}</span>
                                            <button 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(generatedLink);
                                                    toast.success("Link Copied Successfully");
                                                }}
                                                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap ml-2 flex items-center"
                                            >
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                </svg>
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                {generatedToken && (
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Token (for reference):</p>
                                        <div className="flex justify-between w-full p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 dark:bg-dark_50 bg-[#f5f5f5]">
                                            <span className="w-full max-w-11/12 overflow-hidden text-ellipsis text-zinc-700 dark:text-zinc-300">{generatedToken}</span>
                                            <button 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(generatedToken);
                                                    toast.success("Token Copied Successfully");
                                                }}
                                                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap ml-2 flex items-center"
                                            >
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                </svg>
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'list' && (
                            <div className="bg-white dark:bg-dark_50 rounded-lg shadow-lg p-6">
                                <div className="mb-6">
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <input
                                            type="text"
                                            className="form-input flex-1 text-gray-300"
                                            placeholder="Search by email..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <select
                                            className="form-input w-full sm:w-48 text-gray-300"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="pending">Pending</option>
                                            <option value="first_payment_done">First Payment Done</option>
                                            <option value="all_payments_done">All Paid</option>
                                            <option value="defaulted">Defaulted</option>
                                            <option value="expired">Expired</option>
                                        </select>
                                    </div>
                                </div>

                                {isLoadingUsers ? (
                                    <div className="flex justify-center items-center py-8">
                                        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-dark_50">
                                                <tr>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount Paid</th>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Amount</th>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Currency</th>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                                                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-dark_50 divide-y divide-gray-200 dark:divide-gray-700">
                                                {filteredUsers.map((user, index) => (
                                                    <tr 
                                                        key={index} 
                                                        onClick={() => handleRowClick(user)}
                                                        className="transition-colors duration-200 ease-in-out hover:bg-zinc-800 dark:hover:bg-dark_100 cursor-pointer group"
                                                    >
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                                            {getCurrencySymbol(user.currency)}{user.amountPaid}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                                            {getCurrencySymbol(user.currency)}{user.totalAmount}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                                            {user.currency}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                                            {new Date(user.created_at).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                                            {(user.status !== "pending" && user.status !== "expired" && user.status!=="all_payments_done") ? new Date(new Date(user.created_at).getTime() + (30 * 24 * 60 * 60 * 1000)).toLocaleDateString() : "-"}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full transition-all duration-200 ${
                                                                user.status === 'all_payments_done' 
                                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 group-hover:bg-green-200 dark:group-hover:bg-green-900/50'
                                                                    : user.status === 'first_payment_done'
                                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50'
                                                                    : user.status === 'defaulted'
                                                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 group-hover:bg-red-200 dark:group-hover:bg-red-900/50'
                                                                    : user.status === 'expired'
                                                                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 group-hover:bg-gray-200 dark:group-hover:bg-gray-900/50'
                                                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-900/50'
                                                            }`}>
                                                                {(() => {
                                                                    switch(user.status) {
                                                                        case 'all_payments_done':
                                                                            return 'All Paid';
                                                                        case 'first_payment_done':
                                                                            return 'First Payment Done';
                                                                        case "defaulted":
                                                                            return "Defaulter"
                                                                        case "expired":
                                                                            return "Expired"
                                                                        default:
                                                                            return 'Pending';
                                                                    }
                                                                })()}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {/* User Details Modal */}
            {isModalOpen && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-dark_50 rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">User Details</h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedUser.email}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedUser.phone_number || 'Not provided'}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment Status</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                    {(() => {
                                        switch(selectedUser.status) {
                                            case 'all_payments_done':
                                                return 'All Paid';
                                            case 'first_payment_done':
                                                return 'First Payment Done';
                                            case "defaulted":
                                                return "Defaulter"
                                            case "expired":
                                                return "Expired"
                                            default:
                                                return 'Pending';
                                        }
                                    })()}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount Details</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                    Paid: {getCurrencySymbol(selectedUser.currency)}{selectedUser.amountPaid}<br />
                                    Total: {getCurrencySymbol(selectedUser.currency)}{selectedUser.totalAmount}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageEmiOption;

// Status indicator component
const StatusIndicator = ({ status, message }) => {
    if (status === null) return null;
    
    return (
        <div className={`mt-4 p-4 rounded-lg ${
            status === 'success' ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-900' : 
            'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-900'
        }`}>
            <div className="flex items-start">
                <div className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-full ${
                    status === 'success' ? 'bg-green-500' : 'bg-red-500'
                }`}>
                    {status === 'success' ? (
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                    ) : (
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    )}
                </div>
                <span className={`ml-3 text-sm ${
                    status === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                }`}>
                    {message}
                </span>
            </div>
        </div>
    );
};
